import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import {
	defaultTQC3020_R01ProductResultDto,
	TQC3020_R01ProductResultDto,
} from '../Dto_TQC3020_R01';
import { useTQC3020_R01Store } from '../Store_TQC3020_R01';

const PRODUCT_RESULT_COLUMN_STATE_KEY = 'TQC3020_R01-product-result-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC3020_R01ProductResultDto> | null>;
	onRowClick?: (data: TQC3020_R01ProductResultDto) => void;
}

export const BottomTableProductResult_TQC3020_R01 = ({ gridRef, onRowClick }: Props) => {
	const {
		TQC3020_R01productResultList,
		selectedTQC3020_R01ProductResult,
		setSelectedTQC3020_R01ProductResult,
		TQC3020_R01productResultColumnState,
		setTQC3020_R01ProductResultColumnState,
		TQC3020ScrollTop,
		setTQC3020ScrollTop,
	} = useTQC3020_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'productResultNo',
		gridData: TQC3020_R01productResultList,
		defaultGridData: {} as TQC3020_R01ProductResultDto,
		selectedRow: undefined,
	});

	const baseColumnDefs: ColDef<TQC3020_R01ProductResultDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo', width: 130,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '합격수량', colId: 'acceptQty', field: 'acceptQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '생산일자', colId: 'productDt', field: 'productDt', width: 100,
			valueFormatter: (p) => {
				if (!p.value) return '';
				try {
					return formatDate(new Date(p.value));
				} catch {
					return p.value;
				}
			},
		},
		{
			headerName: '작업자코드', colId: 'productEmp', field: 'productEmp', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '작업자명', colId: 'empNm', field: 'empNm', width: 100,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '총생산', colId: 'productQty', field: 'productQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: '불량수', colId: 'badQty', field: 'badQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC3020_R01ProductResultDto>[] = useMemo(() => {
		const merged = TQC3020_R01productResultColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<TQC3020_R01ProductResultDto>({
				baseColumnDefs,
				columnState: TQC3020_R01productResultColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['productResultNo']));
	}, [TQC3020_R01productResultColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRowData = useMemo(() => ([{
		productResultNo: `${TQC3020_R01productResultList.length.toLocaleString()}건`,
		acceptQty: TQC3020_R01productResultList.reduce((sum, item) => sum + (item.acceptQty || 0), 0).toLocaleString(),
		productQty: TQC3020_R01productResultList.reduce((sum, item) => sum + (item.productQty || 0), 0).toLocaleString(),
		badQty: TQC3020_R01productResultList.reduce((sum, item) => sum + (item.badQty || 0), 0).toLocaleString(),
	}]), [TQC3020_R01productResultList]);

	const handleRowClick = useCallback((e: RowClickedEvent<TQC3020_R01ProductResultDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		setSelectedTQC3020_R01ProductResult(rowData);
		onRowClick?.(rowData);
	}, [setSelectedTQC3020_R01ProductResult, onRowClick]);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>Lot 생산 실적</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<TQC3020_R01ProductResultDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedTQC3020_R01ProductResult(undefined)}
					defaultRowData={defaultTQC3020_R01ProductResultDto}
					baseColumnDefs={TQC3020_R01productResultColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setTQC3020_R01ProductResultColumnState}
					localStorageKey={PRODUCT_RESULT_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<TQC3020_R01ProductResultDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<TQC3020_R01ProductResultDto>({
							idKey: 'productResultNo',
							selectedList: selectedTQC3020_R01ProductResult?.productResultNo
								? [selectedTQC3020_R01ProductResult.productResultNo]
								: [],
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={PRODUCT_RESULT_COLUMN_STATE_KEY}
						setColumnState={setTQC3020_R01ProductResultColumnState}
						scrollTop={TQC3020ScrollTop.productResult}
						setScrollTop={(top) => setTQC3020ScrollTop('productResult', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
